import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface PipelineMonthlyData {
  year: number;
  month: number;
  production: number | null;
  sales_target: number | null;
  planned_deliveries: number | null;
}

export interface PotentialSalesOrg {
  orgName: string;
  orgId: string;
  monthlyData: Record<number, number>; // month (1-12) -> camera count
}

interface PipelineData {
  manualData: Record<number, PipelineMonthlyData>; // keyed by month (1-12)
  stock: number; // unallocated devices
  ordersByMonth: Record<number, number>; // month -> count
  outstandingDevicesByMonth: Record<number, number>; // month -> outstanding device count
  deliveredByMonth: Record<number, number>; // month -> delivered device count
  salesByMonth: Record<number, number>; // month -> completed/delivered order device count
  salesByOrg: PotentialSalesOrg[]; // sales broken down by organization
  opportunitiesByMonth: Record<number, number>; // month -> count
  proposalsByMonth: Record<number, number>; // month -> count of proposals sent
  receivedByMonth: Record<number, number>; // month -> new devices received into stock
  potentialSalesByOrg: PotentialSalesOrg[];
}

export const usePipelineData = (year: number) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["pipeline-data", year],
    queryFn: async (): Promise<PipelineData> => {
      const yearStart = `${year}-01-01T00:00:00.000Z`;
      const yearEnd = `${year + 1}-01-01T00:00:00.000Z`;

      // Run all independent queries in parallel
      const [
        manualResult,
        totalDevicesResult,
        allocatedResult,
        ordersResult,
        shipmentsResult,
        dealsResult,
        receivedResult,
        questionnairesResult,
        proposalsResult,
      ] = await Promise.all([
        supabase.from("pipeline_monthly_data" as any).select("*").eq("year", year),
        supabase.from("device_details").select("device_id", { count: "exact", head: true }),
        supabase.from("device_assignments").select("device_id"),
        supabase.from("orders")
          .select("id, created_at, total_cameras, allocated_device_ids, sale_id, status, shipped_at, organization_id")
          .not("status", "eq", "cancelled")
          .gte("created_at", yearStart)
          .lt("created_at", yearEnd),
        supabase.from("order_shipments")
          .select("shipped_at, allocated_device_ids, order_id")
          .not("allocated_device_ids", "eq", "{}")
          .gte("shipped_at", yearStart)
          .lt("shipped_at", yearEnd),
        supabase.from("crm_deals")
          .select("created_at, total_cameras")
          .eq("archived", false)
          .not("status", "in", '("won","lost")')
          .gte("created_at", yearStart)
          .lt("created_at", yearEnd),
        supabase.from("device_details")
          .select("last_synced_at")
          .not("device_serial", "is", null)
          .gte("last_synced_at", yearStart)
          .lt("last_synced_at", yearEnd),
        supabase.from("organization_questionnaires")
          .select("organization_id, form_data, organizations(name)"),
        supabase.from("crm_deals")
          .select("proposal_sent_at")
          .eq("archived", false)
          .not("proposal_sent_at", "is", null)
          .gte("proposal_sent_at", yearStart)
          .lt("proposal_sent_at", yearEnd),
      ]);

      // 1. Manual data
      const manualData: Record<number, PipelineMonthlyData> = {};
      if (manualResult.data) {
        for (const row of manualResult.data as any[]) {
          manualData[row.month] = {
            year: row.year,
            month: row.month,
            production: row.production,
            sales_target: row.sales_target,
            planned_deliveries: row.planned_deliveries,
          };
        }
      }

      // 2. Stock
      const allocatedIds = new Set((allocatedResult.data || []).map((d) => d.device_id));
      const stock = (totalDevicesResult.count || 0) - allocatedIds.size;

      // 3. Sale value map for commercial filter (single extra query, only if needed)
      const allOrders = ordersResult.data || [];
      const saleIds = [...new Set(allOrders.filter(o => o.sale_id).map(o => o.sale_id))];
      const saleValueMap = new Map<string, number>();
      if (saleIds.length > 0) {
        const { data: salesWithDeals } = await supabase
          .from("sales")
          .select("id, deal_id, crm_deals(value)")
          .in("id", saleIds);
        if (salesWithDeals) {
          for (const s of salesWithDeals as any[]) {
            saleValueMap.set(s.id, parseFloat(s.crm_deals?.value?.toString() || '0'));
          }
        }
      }

      // 4. Process orders
      const ordersByMonth: Record<number, number> = {};
      const salesByMonth: Record<number, number> = {};

      // Collect outstanding order info for cumulative calculation
      interface OutstandingOrder {
        createdMonth: number;
        resolvedMonth: number | null; // month when delivered/completed, or null if still open
        outstanding: number;
      }
      const outstandingOrders: OutstandingOrder[] = [];

      // Build shipped device counts per order from shipments data
      const shippedPerOrder = new Map<string, number>();
      if (shipmentsResult.data) {
        for (const s of shipmentsResult.data as any[]) {
          const count = Array.isArray(s.allocated_device_ids) ? s.allocated_device_ids.length : 0;
          shippedPerOrder.set(s.order_id, (shippedPerOrder.get(s.order_id) || 0) + count);
        }
      }

      // Track sales by organization for breakdown
      const orgSalesMap = new Map<string, { orgId: string; monthlyData: Record<number, number> }>();

      for (const o of allOrders) {
        if (!o.sale_id) continue;
        const dealValue = saleValueMap.get(o.sale_id) || 0;
        if (dealValue <= 0) continue;

        const m = new Date(o.created_at!).getMonth() + 1;
        ordersByMonth[m] = (ordersByMonth[m] || 0) + (o.total_cameras || 0);
        salesByMonth[m] = (salesByMonth[m] || 0) + (o.total_cameras || 0);

        // Group by organization
        const orgId = o.organization_id || 'unknown';
        if (!orgSalesMap.has(orgId)) {
          orgSalesMap.set(orgId, { orgId, monthlyData: {} });
        }
        const orgEntry = orgSalesMap.get(orgId)!;
        orgEntry.monthlyData[m] = (orgEntry.monthlyData[m] || 0) + (o.total_cameras || 0);

        // Calculate outstanding: total ordered minus devices actually shipped/delivered
        const shipped = shippedPerOrder.get(o.id) || 0;
        const fallbackAllocated = Array.isArray(o.allocated_device_ids) ? o.allocated_device_ids.length : 0;
        const outstanding = (o.total_cameras || 0) - Math.max(shipped, fallbackAllocated);
        if (outstanding > 0) {
          // Determine when this order was resolved (delivered/completed)
          let resolvedMonth: number | null = null;
          if (["delivered", "completed"].includes(o.status) && o.shipped_at) {
            resolvedMonth = new Date(o.shipped_at).getMonth() + 1;
          }
          outstandingOrders.push({ createdMonth: m, resolvedMonth, outstanding });
        }
      }

      // Fetch org names for sales breakdown
      const orgIds = [...orgSalesMap.keys()].filter(id => id !== 'unknown');
      const orgNameMap = new Map<string, string>();
      if (orgIds.length > 0) {
        const { data: orgs } = await supabase
          .from("organizations")
          .select("id, name")
          .in("id", orgIds);
        if (orgs) {
          for (const org of orgs) {
            orgNameMap.set(org.id, org.name);
          }
        }
      }

      const salesByOrg: PotentialSalesOrg[] = [...orgSalesMap.entries()].map(([orgId, entry]) => ({
        orgId,
        orgName: orgNameMap.get(orgId) || "Unknown",
        monthlyData: entry.monthlyData,
      }));

      // Outstanding devices: show total unfulfilled as a snapshot in the current month only.
      // Orders not yet delivered carry forward to the current month.
      const outstandingDevicesByMonth: Record<number, number> = {};
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      if (year === currentYear || year < currentYear) {
        const snapshotMonth = year === currentYear ? currentMonth : 12;
        let total = 0;
        for (const oo of outstandingOrders) {
          if (oo.createdMonth > snapshotMonth) continue;
          if (oo.resolvedMonth !== null && oo.resolvedMonth <= snapshotMonth) continue;
          total += oo.outstanding;
        }
        if (total > 0) {
          outstandingDevicesByMonth[snapshotMonth] = total;
        }
      }

      // 5. Delivered devices from shipments (commercial only)
      const commercialOrderIds = new Set(
        allOrders
          .filter(o => o.sale_id && (saleValueMap.get(o.sale_id) || 0) > 0)
          .map(o => o.id)
      );
      const deliveredByMonth: Record<number, number> = {};
      const orderIdsWithShipments = new Set<string>();
      if (shipmentsResult.data) {
        for (const s of shipmentsResult.data as any[]) {
          if (!s.shipped_at || !commercialOrderIds.has(s.order_id)) continue;
          orderIdsWithShipments.add(s.order_id);
          const m = new Date(s.shipped_at).getMonth() + 1;
          const count = Array.isArray(s.allocated_device_ids) ? s.allocated_device_ids.length : 0;
          deliveredByMonth[m] = (deliveredByMonth[m] || 0) + count;
        }
      }

      // Fallback: commercial orders marked delivered/completed without shipment records
      for (const o of allOrders) {
        if (!commercialOrderIds.has(o.id)) continue;
        if (!["delivered", "completed"].includes(o.status)) continue;
        if (!o.shipped_at || orderIdsWithShipments.has(o.id)) continue;
        const m = new Date(o.shipped_at).getMonth() + 1;
        const count = Array.isArray(o.allocated_device_ids) ? o.allocated_device_ids.length : 0;
        if (count > 0) {
          deliveredByMonth[m] = (deliveredByMonth[m] || 0) + count;
        }
      }

      // 6. Opportunities
      const opportunitiesByMonth: Record<number, number> = {};
      if (dealsResult.data) {
        for (const d of dealsResult.data) {
          const m = new Date(d.created_at!).getMonth() + 1;
          opportunitiesByMonth[m] = (opportunitiesByMonth[m] || 0) + ((d as any).total_cameras || 0);
        }
      }

      // 6b. Proposals
      const proposalsByMonth: Record<number, number> = {};
      if (proposalsResult.data) {
        for (const d of proposalsResult.data as any[]) {
          const m = new Date(d.proposal_sent_at).getMonth() + 1;
          proposalsByMonth[m] = (proposalsByMonth[m] || 0) + 1;
        }
      }

      // 7. Received into Stock (new devices by month of last_synced_at)
      const receivedByMonth: Record<number, number> = {};
      if (receivedResult.data) {
        for (const d of receivedResult.data) {
          const m = new Date(d.last_synced_at).getMonth() + 1;
          receivedByMonth[m] = (receivedByMonth[m] || 0) + 1;
        }
      }

      // 8. Potential Sales from questionnaires
      const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const yearKey = year.toString();
      const potentialSalesByOrg: PotentialSalesOrg[] = [];
      if (questionnairesResult.data) {
        for (const q of questionnairesResult.data as any[]) {
          const fd = q.form_data;
          const expansion = fd?.monthlyExpansion?.[yearKey];
          if (!expansion) continue;
          const monthlyData: Record<number, number> = {};
          let hasAny = false;
          for (let i = 0; i < 12; i++) {
            const val = expansion[MONTH_NAMES[i]] || 0;
            if (val > 0) { monthlyData[i + 1] = val; hasAny = true; }
          }
          if (hasAny) {
            potentialSalesByOrg.push({
              orgName: q.organizations?.name || "Unknown",
              orgId: q.organization_id,
              monthlyData,
            });
          }
        }
      }

      return { manualData, stock, ordersByMonth, outstandingDevicesByMonth, deliveredByMonth, salesByMonth, salesByOrg, opportunitiesByMonth, proposalsByMonth, receivedByMonth, potentialSalesByOrg };
    },
    staleTime: 5 * 60 * 1000,
  });

  const upsertMutation = useMutation({
    mutationFn: async ({
      month,
      field,
      value,
    }: {
      month: number;
      field: "production" | "sales_target" | "planned_deliveries";
      value: number | null;
    }) => {
      // Try update first, then insert
      const { data: existing } = await supabase
        .from("pipeline_monthly_data" as any)
        .select("id")
        .eq("year", year)
        .eq("month", month)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("pipeline_monthly_data" as any)
          .update({
            [field]: value,
            updated_at: new Date().toISOString(),
            updated_by: user?.id || null,
          } as any)
          .eq("year", year)
          .eq("month", month);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("pipeline_monthly_data" as any)
          .insert({
            year,
            month,
            [field]: value,
            updated_by: user?.id || null,
          } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipeline-data", year] });
    },
  });

  return { ...query, upsertMutation };
};
